/**
 * DecomposeExplainer — explainer for the DECOMPOSE Structural Duality Principle (De).
 *
 * DECOMPOSE is the level-2 SDP that pairs with COMPOSE along the
 * operad ↔ cooperad adjunction. It certifies that safe composition is not
 * safe extraction — a COMPOSE witness can be stacked through arbitrary
 * decomposition without producing a forbidden pattern. With COMPOSE
 * (primal) it closes as the ICG-De Safety Envelope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  CalloutBox,
  ExplainerCard,
  GradedAdjunctionDiagram,
  SdpLevelBadge,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.DECOMPOSE.color;
const LEVEL = COMPOSITIONALITY_PRINCIPLE_COLOR.DECOMPOSE.level;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-decompose",
  kind: `SSM · Compositionality Principle (S2 · level ${LEVEL})`,
  title: "DECOMPOSE",
  subtitle: "De · operad ↔ cooperad · Neutralization Theorem: C",
  accent: ACCENT,
  accentAlt: THEOREM_COLOR.COMPOSE,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "COMPOSE (C, primal Neutralization Theorem)",
    href: "#explainer-compose",
    accent: THEOREM_COLOR.COMPOSE,
  },
  { label: "ICG-De (Safety Envelope)", href: "#explainer-icg-de", accent: THEOREM_COLOR.COMPOSE },
  { label: "Operad", href: "#explainer-operad" },
  { label: "Cooperad", href: "#explainer-cooperad" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>DECOMPOSE</strong> is the safety story for taking things apart: it certifies that
      "safe-in implies safe-out under composition" does not silently imply safe extraction — by the
      operad ↔ cooperad duality, not by inspection.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you can write "these safe parts compose to a safe whole" (a COMPOSE witness), you
        immediately want to pull a sub-component back out. DECOMPOSE is the certificate that says
        extraction is governed — decomposition (cooperad) carries explicit residual dependencies, so
        a safe composite cannot be silently re-split into an unsafe part.
      </p>
      <p>
        Where COMPOSE delivers the guarantee inside one regime, DECOMPOSE delivers compositionality
        across extraction. Together they close as the ICG-De Safety Envelope.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        DECOMPOSE is the level-2 Structural Duality Principle that pairs the safety operad
        (composition) with its cooperad (decomposition with residual dependencies) as a{" "}
        <strong>graded</strong> adjunction.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        <strong>1-categorical seam vs SDP graded adjunction</strong> (per{" "}
        <code>theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md</code>). At the 1-categorical level
        the COMPOSE seam declares an <em>absolute empty hom-set</em>: injecting an unsafe argument
        into the safe algebra either is a morphism or it is not, in the strict binary sense. As a{" "}
        <strong>level-2</strong> SDP, DECOMPOSE operates through a 2-categorical <em>graded</em>{" "}
        adjunction indexed by residual dependency: its counit supplies, at each grade, the exact
        safety waiver a bridge must produce to extract a sub-term — structurally inexpressible
        without the witness, expressible with it. Level-2 SDPs come from higher-categorical
        adjunctions for singleton Neutralization Theorems (here, C alone).
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        DECOMPOSE is what lets the ICG-De Safety Envelope reduce the audit surface from "a COMPOSE
        witness per extraction" to "one envelope" carrying the graded adjunction unit and counit.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If a decomposition stacks but the extracted part
        is not a COMPOSE witness, DECOMPOSE rules it out structurally — no graded-adjunction counit
        certifies the split. Re-splitting a safe composite into an unsafe gadget is{" "}
        <em>structurally inexpressible</em> at the SDP level.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-De" accent={ACCENT}>
        COMPOSE (Neutralization Theorem) ⊗ DECOMPOSE (SDP) = ICG-De, the single two-sided
        certificate. Audit surface collapses from{" "}
        <em>(Neutralization Theorem witness) + (SDP witness)</em> to <em>one envelope</em> carrying
        the operad ⊣ cooperad unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — SDPs and ICGs (§7, §7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Authoritative treatment of the empty-hom-set seam vs the graded SDP adjunction; AGENTS.md routes here.",
    },
    {
      label: "COMPOSE kernel module",
      path: "agda/Kernel/COMPOSE/Neutralization.agda",
    },
    {
      label: "DECOMPOSE compositionality module",
      path: "agda/Kernel/CompositionalityPrinciple/DECOMPOSE.agda",
    },
  ],
};

const DIAGRAM = (
  <>
    <div style={{ marginBottom: "0.5rem" }}>
      <SdpLevelBadge level={LEVEL} accent={ACCENT} />
    </div>
    <GradedAdjunctionDiagram
      accent={ACCENT}
      grade="r"
      ariaLabel="DECOMPOSE graded adjunction: composition operad ⊣ decomposition cooperad, indexed by residual-dependency grade r"
    />
  </>
);

export function DecomposeExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
